import { useCallback, useMemo } from "react";
import {
  CategoryStandardProduct,
  CreateCartPayload,
} from "shared/api/middleware-api-client";
import {
  PANEL_CATEGORY_CODE,
  PANEL_SET_CODES,
  EINDPROFIEL_CATEGORY_CODE,
  BASE_INCLUDED_PANELS,
} from "../../../../shared/utils/standard-product";
import { useStandardProductCategoriesByID } from "shared/api/hooks/useStandardProductCategories";
import { useStandardProductsDeliveryMethod } from "./useStandardProductsDeliveryMethod";
import { isTestEnvironment } from "shared/utils/checkEnvironment";

// env-specific IDs
const IDS = isTestEnvironment()
  ? {
      base: "65eed5c36d63b40200f228ae",
      additional: "65eed61a6d63b40200f228b3",
      eind: "66a8e5ff9aa3ca000211a35e",
    }
  : {
      base: "65eed5c36d63b40200f228ae",
      additional: "65eed61a6d63b40200f228b3",
      eind: "66a0b6292850e600026dad8e",
    };

// Helpers

type Counts = { panelCount: number; eindprofielCount: number };

/** Count panel/eindprofiel items in a single pass. Doubles panel “set” codes. */
function countFromCart(cart: CreateCartPayload): Counts {
  let panelCount = 0;
  let eindprofielCount = 0;

  for (const p of cart.standardProducts) {
    const typeCode = p.productType.code;

    if (typeCode === PANEL_CATEGORY_CODE) {
      const amount = PANEL_SET_CODES.includes(p.code) ? p.amount * 2 : p.amount;
      panelCount += amount;
    } else if (typeCode === EINDPROFIEL_CATEGORY_CODE) {
      eindprofielCount += p.amount;
    }
  }
  return { panelCount, eindprofielCount };
}

/** Build assembly product  based on counts & catalog items. */
function buildAssemblyProducts(
  panelBase: CategoryStandardProduct | null,
  panelAdditional: CategoryStandardProduct | null,
  eindprofiel: CategoryStandardProduct | null,
  counts: Counts,
): CategoryStandardProduct[] {
  if (!panelBase || !panelAdditional || !eindprofiel) return [];

  const { panelCount, eindprofielCount } = counts;

  const products: CategoryStandardProduct[] = [{ ...panelBase, amount: 1 }];

  if (panelCount > BASE_INCLUDED_PANELS) {
    products.push({
      ...panelAdditional,
      amount: panelCount - BASE_INCLUDED_PANELS,
    });
  }

  if (eindprofielCount > 0) {
    products.push({ ...eindprofiel, amount: eindprofielCount });
  }

  return products;
}

function computeAssemblyCosts(
  panelBasePrice: number,
  panelAddPrice: number,
  eindPrice: number,
  counts: Counts,
): number {
  const { panelCount, eindprofielCount } = counts;

  return (
    panelBasePrice +
    (panelCount > BASE_INCLUDED_PANELS
      ? (panelCount - BASE_INCLUDED_PANELS) * panelAddPrice
      : 0) +
    eindPrice * eindprofielCount
  );
}

// ---- hook -------------------------------------------------------------------

export function useStandardProductDeliveryCostsCalculator(
  cart: CreateCartPayload,
  dynamic = false,
  enabled = true,
) {
  const { calculateDeliveryMethod } = useStandardProductsDeliveryMethod();

  // Assembly, eindprofiel product ID's
  const { productsById } = useStandardProductCategoriesByID({ enabled });
  // Catalog products
  const panelBaseAssemblyProduct = productsById?.get(IDS.base) ?? null;
  const panelAdditionalAssemblyProduct =
    productsById?.get(IDS.additional) ?? null;
  const eindprofielAssemblyProduct = productsById?.get(IDS.eind) ?? null;

  // Precompute counts
  const counts = useMemo(() => countFromCart(cart), [cart]);

  // When to calculate assembly
  const shouldCalcAssembly =
    cart.standardProducts.length > 0 &&
    (dynamic || (cart.pickupWarehouse === "" && cart.withAssembly));

  // When to calculate delivery
  const shouldCalcDelivery =
    cart.standardProducts.length > 0 &&
    (dynamic ||
      (((cart.delivery?.date as unknown as string) ||
        cart.pickupWarehouse === "") &&
        !cart.withAssembly));

  // Delivery price
  const standardProductDeliveryCosts = useMemo(() => {
    if (!shouldCalcDelivery) return 0;
    // Calculate delivery method price
    return calculateDeliveryMethod(cart.standardProducts)?.price ?? 0;
  }, [shouldCalcDelivery, calculateDeliveryMethod, cart.standardProducts]);

  // Assembly price
  const standardProductAssemblyCosts = useMemo(() => {
    if (!shouldCalcAssembly) return 0;

    const basePrice = panelBaseAssemblyProduct?.price ?? 0;
    const addPrice = panelAdditionalAssemblyProduct?.price ?? 0;
    const eindPrice = eindprofielAssemblyProduct?.price ?? 0;

    // If any catalog product is missing, treat as 0-cost/no-op
    if (
      !panelBaseAssemblyProduct ||
      !panelAdditionalAssemblyProduct ||
      !eindprofielAssemblyProduct
    ) {
      return 0;
    }

    return computeAssemblyCosts(basePrice, addPrice, eindPrice, counts);
  }, [
    shouldCalcAssembly,
    panelBaseAssemblyProduct,
    panelAdditionalAssemblyProduct,
    eindprofielAssemblyProduct,
    counts,
  ]);

  // Assembly products
  const getAssemblyProducts = useMemo(() => {
    if (!shouldCalcAssembly) return [];
    return buildAssemblyProducts(
      panelBaseAssemblyProduct,
      panelAdditionalAssemblyProduct,
      eindprofielAssemblyProduct,
      counts,
    );
  }, [
    shouldCalcAssembly,
    panelBaseAssemblyProduct,
    panelAdditionalAssemblyProduct,
    eindprofielAssemblyProduct,
    counts,
  ]);

  const getAssemblyProductsForCart = useCallback(
    (c: CreateCartPayload): CategoryStandardProduct[] => {
      if (!shouldCalcAssembly) return [];

      const localCounts = countFromCart(c);
      return buildAssemblyProducts(
        panelBaseAssemblyProduct,
        panelAdditionalAssemblyProduct,
        eindprofielAssemblyProduct,
        localCounts,
      );
    },
    [
      shouldCalcAssembly,
      panelBaseAssemblyProduct,
      panelAdditionalAssemblyProduct,
      eindprofielAssemblyProduct,
    ],
  );

  // Stable return
  return useMemo(
    () => ({
      standardProductAssemblyCosts,
      standardProductDeliveryCosts,
      getAssemblyProducts,
      getAssemblyProductsForCart,
      getPanelTotalProductsCount: counts.panelCount,
    }),
    [
      standardProductAssemblyCosts,
      standardProductDeliveryCosts,
      getAssemblyProducts,
      getAssemblyProductsForCart,
      counts.panelCount,
    ],
  );
}
